/**
 * @fileoverview Photo widget for response-scoped attachment selection.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Request photos from the host-injected picker and render saved references.
 * - Enforce the configured attachment limit and suppress edits when read-only.
 * Dependencies:
 * - React widget contracts and the renderer attachment-picker/thumbnail contexts.
 * Architecture:
 * - The widget owns picker intent and display; the host picker owns local persistence.
 * Notes:
 * - `extras.maxPhotos` defaults to five.
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import type { AttachmentRefLike } from '@firesquirrel/core-domain';
import { useAttachmentPicker, useIsOffline, useThumbnailResolver } from '../contracts/rendererContext';
import { AttachmentThumb } from './AttachmentThumb';

/**
 * @component PhotoField
 * Purpose: Select and display response-scoped inspection photos.
 * Props: BaseWidgetProps<AttachmentRefLike[] | null | undefined>.
 * Behavior: Requests photos, deduplicates attachment references, and respects the maximum.
 * Data Source: local widget values and the host-provided attachment picker.
 * Constraints: No direct network or persistence calls; resolved disabled state blocks edits.
 * Performance: Limits picker results to the remaining attachment capacity.
 */
export function PhotoField(props: BaseWidgetProps<AttachmentRefLike[] | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, bulkPilotMode, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { maxPhotos?: number };
  const picker = useAttachmentPicker();
  const isOffline = useIsOffline();
  const thumbnailResolver = useThumbnailResolver();
  const refs = value ?? [];
  const max = extras.maxPhotos ?? 5;
  const disabledByPolicy = !!resolved?.disabled || !!bulkPilotMode || !picker;

  async function pick() {
    if (disabledByPolicy || refs.length >= max || !picker) return;
    const picked = await picker.pick(
      { parentType: 'response', parentId: field.id, kind: 'photo' },
      { maxCount: max - refs.length },
    );
    const merged = [...refs];
    for (const r of picked) {
      if (!merged.some((m) => m._id === r._id)) merged.push(r);
      if (merged.length >= max) break;
    }
    onChange(merged);
  }

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="photo"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <div className="flex flex-col gap-2">
        <button
          id={field.id}
          type="button"
          data-intent="picker"
          disabled={disabledByPolicy || refs.length >= max}
          onClick={pick}
          className="px-3 py-2 text-sm border border-dashed border-slate-300 rounded-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          {refs.length === 0 ? 'Attach photo' : `Attach another (${refs.length}/${max})`}
        </button>
        {isOffline && (
          <span data-pill="offline" role="status" className="self-start inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
            offline
          </span>
        )}
        {refs.length > 0 && (
          <ul className="text-xs text-slate-600 flex flex-col gap-2" aria-label="Attached photos">
            {refs.map((r, idx) => (
              <li key={r._id} className="flex items-center gap-2">
                {thumbnailResolver && <AttachmentThumb attachmentId={r._id} alt={r.fileName ?? `Photo ${idx + 1}`} />}
                <span data-attachment-id={r._id} className="flex-1 truncate">{r.fileName ?? `Photo ${idx + 1}`}</span>
                {!resolved?.disabled && (
                  <button
                    type="button"
                    onClick={() => onChange(refs.filter((x) => x._id !== r._id))}
                    className="text-red-500 text-xs shrink-0"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </FieldWrapper>
  );
}
